import http from '../../shared/http.js';
import { withFlash } from '../../shared/flash.js';
import { confirmAction, hideLoading, notifyError, showLoading } from '../../shared/notify.js';

// The documents of a record, managed from that record's own page. Its own area
// rather than one under kunden/, because the same module drives every entity's
// section: everything below reads the parent out of the wrapper's dataset
// instead of assuming a customer.
//
// The wrapper carries one `data-<entity>-id`, and the table below maps that to
// the API path its nested routes are mounted under. Adding a section is an entry
// here plus the matching one in controllers/helpers/entitaetScope.js.
const PARENTS = [
  { attribut: 'kundeId', pfad: 'kunden' },
  { attribut: 'kandidatId', pfad: 'kandidaten' }
];

// Which record's documents are on screen, as the path segment the upload posts
// to. Returns null when the wrapper names no parent this module knows, which is
// a template mistake rather than something to guess at.
function parentPfadFor(dataset) {
  const treffer = PARENTS.find((entry) => dataset[entry.attribut]);
  return treffer ? `${treffer.pfad}/${dataset[treffer.attribut]}` : null;
}

// Reloads the page showing a flash. The table is rendered server-side, so a
// round trip is what puts a new row on screen. The list is short enough that the
// reload is cheaper than keeping a client-side copy of it in step.
function reloadWith(key) {
  window.location.href = withFlash(window.location.pathname, key);
}

// The inline message strip inside the upload dialog. Used rather than a toast
// for the upload's own errors: a rejected file is something to fix in the form
// that is still open, and a toast would fade while the person is still reading
// which types are allowed.
function setStatus(text, tone = 'info') {
  const status = document.getElementById('dokument-status');
  if (!status) return;

  if (!text) {
    status.classList.add('hidden');
    status.textContent = '';
    return;
  }

  status.className = `text-sm ${tone === 'error' ? 'text-error' : 'text-base-content/70'}`;
  status.textContent = text;
}

// What the server said went wrong, or a fallback. Every rejection this dialog
// can produce - wrong type, over the cap, no file - is a German 400 from the
// API, so the server's own message is the one worth showing.
function fehlerText(err) {
  return err.response?.data?.message || 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.';
}

export function initDokumentInline() {
  const liste = document.getElementById('dokument-liste');
  if (!liste) return;

  const modal = document.getElementById('dokument-modal');
  const form = document.getElementById('dokument-form');
  const editModal = document.getElementById('dokument-edit-modal');
  const editForm = document.getElementById('dokument-edit-form');
  const parentPfad = parentPfadFor(liste.dataset);
  if (!parentPfad) return;

  document.getElementById('dokument-neu')?.addEventListener('click', () => {
    form.reset();
    setStatus('');
    modal.showModal();
  });

  form.querySelector('[data-action="abbrechen"]')?.addEventListener('click', () => modal.close());
  editForm
    .querySelector('[data-action="abbrechen"]')
    ?.addEventListener('click', () => editModal.close());

  // One delegated listener for the whole table, so rows cost nothing to add.
  // The download is a plain link and deliberately not handled here.
  liste.addEventListener('click', async (event) => {
    const button = event.target.closest('button[data-action]');
    if (!button) return;

    if (button.dataset.action === 'edit') {
      const { id, dateiname, kategorie, beschreibung } = button.dataset;
      editForm.querySelector('#dokument-edit-id').value = id;
      editForm.querySelector('#dok-edit-kategorie').value = kategorie || '';
      editForm.querySelector('#dok-edit-beschreibung').value = beschreibung || '';

      const name = document.getElementById('dokument-edit-name');
      if (name) name.textContent = dateiname;

      editModal.showModal();
      return;
    }

    if (button.dataset.action !== 'delete') return;

    const { id, name } = button.dataset;
    const confirmed = await confirmAction({
      title: 'Dokument löschen',
      message: `Möchten Sie ${name} endgültig löschen? Das kann nicht rückgängig gemacht werden.`,
      okText: 'Löschen'
    });
    if (!confirmed) return;

    button.disabled = true;
    showLoading('Dokument wird gelöscht …');

    try {
      await http.delete(`/dokumente/${id}`);
      reloadWith('dokument-geloescht');
    } catch (err) {
      hideLoading();
      button.disabled = false;
      notifyError(fehlerText(err));
    }
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    const datei = form.querySelector('#dok-datei').files[0];
    if (!datei) {
      setStatus('Bitte wählen Sie eine Datei aus.', 'error');
      return;
    }

    // FormData rather than JSON: this is the one request in the app that
    // carries bytes, and the boundary header is set by the browser.
    const body = new window.FormData();
    body.append('datei', datei);
    body.append('kategorie', form.querySelector('#dok-kategorie').value);
    body.append('beschreibung', form.querySelector('#dok-beschreibung').value.trim());

    const submitButton = document.getElementById('dokument-speichern');
    if (submitButton) submitButton.disabled = true;
    setStatus('Wird hochgeladen … 0 %');

    try {
      // No showLoading here, unlike the other handlers: a 20 MB upload is slow
      // enough that a percentage says something a spinner cannot, and the
      // overlay would hide the dialog it belongs to.
      await http.post(`/${parentPfad}/dokumente`, body, {
        onUploadProgress: (progress) => {
          // `total` is absent when the browser cannot determine the size, in
          // which case there is no percentage to show.
          if (!progress.total) return setStatus('Wird hochgeladen …');
          const prozent = Math.round((progress.loaded * 100) / progress.total);
          setStatus(`Wird hochgeladen … ${prozent} %`);
        }
      });

      reloadWith('dokument-hochgeladen');
    } catch (err) {
      if (submitButton) submitButton.disabled = false;
      setStatus(fehlerText(err), 'error');
    }
  });

  editForm.addEventListener('submit', async (event) => {
    event.preventDefault();

    const id = editForm.querySelector('#dokument-edit-id').value;
    const submitButton = editForm.querySelector('button[type="submit"]');

    if (submitButton) submitButton.disabled = true;
    showLoading('Wird gespeichert …');

    try {
      await http.patch(`/dokumente/${id}`, {
        kategorie: editForm.querySelector('#dok-edit-kategorie').value,
        // Sent as '' when cleared rather than dropped: dropping it would mean
        // "leave unchanged" on a PATCH, so clearing a description would never
        // reach the record.
        beschreibung: editForm.querySelector('#dok-edit-beschreibung').value.trim()
      });

      reloadWith('dokument-aktualisiert');
    } catch (err) {
      hideLoading();
      if (submitButton) submitButton.disabled = false;
      notifyError(fehlerText(err));
    }
  });
}
